import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useActions, useAppData } from '../data';
import { STEPS, type WalkthroughStep } from './steps';

type WalkthroughContextValue = {
  /** True while the bottom panel is visible. */
  active: boolean;
  /** Current step index (0..STEPS.length - 1). */
  stepIndex: number;
  /** Convenience accessor for the current step definition. */
  step: WalkthroughStep;
  totalSteps: number;
  /** True when the user is on the final step. */
  isLast: boolean;
  /** Open the panel at the first step. Resets the in-memory progress. */
  start: () => void;
  /** Advance one step; on the last step, finish + dismiss. */
  next: () => void;
  /** Step backward one. No-op on the first step. */
  back: () => void;
  /** Dismiss the panel and mark the walkthrough complete in the store. */
  skip: () => void;
};

const WalkthroughContext = createContext<WalkthroughContextValue | null>(null);

export function useWalkthrough(): WalkthroughContextValue {
  const ctx = useContext(WalkthroughContext);
  if (!ctx) {
    throw new Error('useWalkthrough must be used inside <WalkthroughProvider>');
  }
  return ctx;
}

/**
 * Owns the walkthrough's runtime state. The `walkthroughComplete` flag
 * on profile is the persisted "has the user ever finished it" bit; this
 * provider owns the in-memory active+step state so the panel can show or
 * hide independently (replay from Settings doesn't reset the persisted
 * flag).
 *
 * Auto-start: once profile.onboardingComplete flips to true and
 * walkthroughComplete is still false, we start the panel automatically.
 * We only do this once per mount via a ref so dismissing doesn't
 * immediately re-open.
 */
export function WalkthroughProvider({ children }: { children: ReactNode }) {
  const data = useAppData();
  const actions = useActions();
  const autoStarted = useRef(false);
  const [active, setActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  const start = useCallback(() => {
    setStepIndex(0);
    setActive(true);
  }, []);

  const finish = useCallback(() => {
    setActive(false);
    setStepIndex(0);
    if (!data.profile.walkthroughComplete) {
      actions.markWalkthroughComplete();
    }
  }, [actions, data.profile.walkthroughComplete]);

  const next = useCallback(() => {
    setStepIndex((i) => {
      if (i >= STEPS.length - 1) {
        finish();
        return i;
      }
      return i + 1;
    });
  }, [finish]);

  const back = useCallback(() => {
    setStepIndex((i) => Math.max(0, i - 1));
  }, []);

  const skip = useCallback(() => {
    finish();
  }, [finish]);

  // Auto-start once after onboarding completes. We guard with the ref so
  // re-opening the panel manually doesn't re-trigger this branch.
  useEffect(() => {
    if (autoStarted.current) return;
    if (
      data.profile.onboardingComplete &&
      !data.profile.walkthroughComplete
    ) {
      autoStarted.current = true;
      start();
    }
  }, [
    data.profile.onboardingComplete,
    data.profile.walkthroughComplete,
    start,
  ]);

  const step = STEPS[stepIndex] ?? STEPS[0]!;
  const isLast = stepIndex === STEPS.length - 1;

  const value = useMemo<WalkthroughContextValue>(
    () => ({
      active,
      stepIndex,
      step,
      totalSteps: STEPS.length,
      isLast,
      start,
      next,
      back,
      skip,
    }),
    [active, stepIndex, step, isLast, start, next, back, skip],
  );

  return (
    <WalkthroughContext.Provider value={value}>
      {children}
    </WalkthroughContext.Provider>
  );
}
